<template>
  <div data-component-group="payment-form" data-component="amount">
    <slot name="beforeForm" />

    <form @submit.prevent="onSubmit">
      <div>
        <magic-container>
          <v-text-field
            v-model="subtotal"
            prepend-inner-icon="receipt"
            :label="$t('subtotal')"
            :prefix="currency"
            type="text"
            required
            readonly
          />
          <v-text-field
            v-model="discount"
            prepend-inner-icon="receipt"
            :label="$t('discount')"
            prefix="%"
            type="text"
            required
            :readonly="isDisabled"
            :error-messages="discountErrors"
            @blur="v$.discount.$touch()"
            @update:model-value="onChangeDiscount"
          />
        </magic-container>
        <magic-container>
          <v-text-field
            v-model="capAmount"
            prepend-inner-icon="receipt"
            :label="$t('capping_amount')"
            :prefix="currency"
            type="text"
            required
            readonly
          />
          <v-text-field
            v-model="capPercent"
            prepend-inner-icon="receipt"
            :label="$t('capping_discount')"
            prefix="%"
            type="text"
            required
            readonly
          />
        </magic-container>
        <magic-container>
          <v-text-field
            v-model="total"
            prepend-inner-icon="receipt"
            :prefix="currency"
            :label="$t('total')"
            type="text"
            required
            readonly
          />
        </magic-container>
      </div>

      <div class="form-actions">
        <v-btn v-if="onCancel" color="secondary" variant="flat" @click="cancel">
          {{ cancelTriggerLabel }}
        </v-btn>

        <v-btn
          v-if="onSubmit && !isDisabled"
          color="secondary"
          class="trigger"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          <v-icon>label</v-icon>
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required, between } from '@vuelidate/validators';
  import { isObj, isNum } from '../../../utils';
  import { inject } from 'vue';

  export default {
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        total: {
          required,
        },
        subtotal: {
          required,
        },
        discount: {
          required,
          between: between(0, (100 - this.capPercent))
        },
      };
    },
    props: {
      payment: {
        type: Object,
        default: undefined,
      },
      cancelTriggerLabel: {
        type: String,
        default() {
          // return this.$t('cancel');
          return inject('i18n').t('cancel');
        },
      },
      confirmTriggerLabel: {
        type: String,
        default() {
          // return this.$t('add_payment');
          return inject('i18n').t('add_payment');
        },
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
    },
    data() {
      return {
        total: 0,
        capAmount: 0,
        capPercent: 0,
        discount: 0,
        subtotal: 0,
        isSubmitted: false,
      };
    },
    computed: {
      hasItem() {
        return isObj(this.payment);
      },
      isOpened() {
        const { status } = this.payment;
        const { name = '' } = status;
        return name.toLowerCase() === 'open';
      },
      currency() {
        return this.hasItem && isObj(this.payment.currency)
          ? this.payment.currency.symbol
          : '';
      },
      discountErrors() {
        return this.v$.discount.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('discount_required');
          }
          if (error.$validator === 'between') {
            return this.$t('discount_range', { max: 100 - this.capPercent });
          }
          return error.$message;
        });
      },
    },
    watch: {
      payment(nextPayment) {
        this.clear();
        this.onReceivePayment(nextPayment);
      },
    },
    created() {
      this.onReceivePayment(this.payment);
    },
    methods: {
      onReceivePayment(nextPayment) {
        if (isObj(nextPayment) && isNum(nextPayment.id)) {
          this.extractPaymentData(nextPayment);
        }
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        return this.updateAmount();
      },
      clear() {
        this.v$.$reset();
        this.subtotal = 0;
        this.discount = 0;
        this.capAmount = 0;
        this.capPercent = 0;
        this.total = 0;
        this.isSubmitted = false;
      },
      updateAmount() {
        const { discount } = this;

        const amount = {
          discount,
        };

        this.isSubmitted = true;

        return this.onSubmit(amount).then((res) => {
          if (!res) {
            this.isSubmitted = false;
            return;
          }

          const hasErrors = this.$store.getters['alerts/error'].length > 0;
          if (hasErrors) {
            return false;
          }

          this.clear();
          if (res.data) this.extractPaymentData(res.data);

          return res;
        });
      },
      canSubmitRequest() {
        const { subtotal, discount, capPercent, total, isSubmitted } = this;

        return (
          subtotal > 0 && Number(discount) <= (100 - capPercent) && total >= 0 && parseFloat(discount) >= 0 && !isSubmitted
        );
      },
      extractPaymentData({ amount }) {
        const { subtotal, discount, total, cap_amount, cap_percent } = amount;

        this.subtotal = subtotal;
        this.discount = parseFloat(discount);
        this.capAmount = cap_amount;
        this.capPercent = cap_percent;
        this.total = total;
      },
      onChangeDiscount(discount) {
        const totalDiscount = (Number(discount) + this.capPercent) * (this.subtotal / 100);
        this.total = (this.subtotal - totalDiscount).toFixed(2);
        if (Number(this.total) < 0) {
          this.total = 0;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='payment-form'][data-component='amount'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin-top: 0;
      margin-bottom: 0;

      &.trigger {
        .v-icon {
          margin: 0 6px 0 0;
        }
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
